import { useRef, useEffect, useCallback, useState } from 'react';
import type { FormEvent, FC } from 'react';

import { Logger } from '../../../../logger/Logger';
import { useAutoplayHiddenFlag } from '../../../../store/compositeSelectors/useAutoplayHiddenFlag';
import { useCoreDispatch } from '../../../../store/CoreStore';
import { useAutoplaySlice } from '../../../../store/slices/autoplay/selectors';
import { autoplayActions } from '../../../../store/slices/autoplay/slice';
import { AutoplayStatus } from '../../../../store/slices/autoplay/types';
import { useBetSlice } from '../../../../store/slices/bets/selectors';
import { useGameStateSlice } from '../../../../store/slices/game/selectors';
import { toastActions } from '../../../../store/slices/toast/slice';
import { CurrencyConnected } from '../../../currency/CurrencyConnected';
import { AutoPlayView } from '../../autoPlay/AutoPlay';
import { AutoPlayLimitInputsConnected, AutoPlayLimitRef } from '../../autoPlay/AutoplayLimitsConnected';
import type { GameButtonProps } from '../../gameButton/gameButton';

import { placeAutoplayBet } from './utils/placeAutoplayBet';

export interface AutoplayModalConnectedProps extends Omit<GameButtonProps, 'icon'> {}

export const AutoplayModalConnected: FC<AutoplayModalConnectedProps> = () => {
  const dispatch = useCoreDispatch();
  const hideAutoplay = useAutoplayHiddenFlag();
  const limitsRef = useRef<AutoPlayLimitRef>(null);
  const [isPageVisible, setIsPageVisible] = useState(true); // Used to force refresh the component on update

  // BETS
  const totalBet = useBetSlice.totalBet();
  const isBetsOpen = useGameStateSlice.isBetsOpenStatus();
  const balance = useGameStateSlice.balance();
  const digitsNumber = useGameStateSlice.getCurrencyInfo()?.digitsNumber;
  const currentBet = useBetSlice.bets();
  const disabledBets = useBetSlice.getDisabledBetSpots();
  const betAmount = useBetSlice.rawTotalBet();

  // AUTOPLAY
  const isInProgress = useAutoplaySlice.isInProgress();
  const isOpen = useAutoplaySlice.isOpen();
  const savedBet = useAutoplaySlice.getSavedBet();
  const roundsLeft = useAutoplaySlice.getRemainingCount();
  const autoplayStatus = useAutoplaySlice.getAutoplayStatus();

  const bet = useCallback(() => {
    if (isBetsOpen && betAmount === 0) {
      placeAutoplayBet(true, savedBet, disabledBets || [], dispatch, balance, digitsNumber);
    }
  }, [isBetsOpen, betAmount, savedBet, disabledBets, dispatch, balance, digitsNumber]);

  const resume = useCallback((): void => {
    if (roundsLeft > 0) {
      dispatch(autoplayActions.resume());
      /**
       * We must check against old state because in this callback the dispatch will have happened but the callback will not have received updated values at the time of execution.
       * That is why we check if it has been paused or running. If we get Stopped, we don't want to bet.
       */
      if (autoplayStatus === AutoplayStatus.Paused || autoplayStatus === AutoplayStatus.Running) {
        /**
         * Place bets manually in case the AP is unpaused during open bets countdown and have
         * missed the chance to place the bet on bets open start.
         * */
        bet();
      }
    } else {
      /**
       * We must handle case where the game becomes visible during bets open phase after long enough time has passed to lose WS connection.
       * In such a case the check on win results processor might be missed that stops the auotplay at 0 rounds left.
       * Therefore we must check if rounds left is 0 and if so, stop the autoplay here.
       */
      if (autoplayStatus === AutoplayStatus.Paused || autoplayStatus === AutoplayStatus.Running) {
        dispatch(autoplayActions.stop());
      }
    }
  }, [dispatch, bet, autoplayStatus, roundsLeft]);

  const pause = useCallback((): void => {
    dispatch(autoplayActions.pause());
  }, [dispatch]);

  const handleVisibilityChange = useCallback((): void => {
    setIsPageVisible(!document.hidden);
    if (!document.hidden) {
      resume();
    } else {
      pause();
    }
  }, [pause, resume]);

  // Add event listener for visibility change to pause/resume autoplay for mobile devices
  useEffect(() => {
    window.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleVisibilityChange]);

  if (hideAutoplay) return null;

  const onStop = (): void => {
    dispatch(toastActions.createToast('AUTOPLAY_STOPPED'));
    toggleOpen();
    Logger.logEvent('click', 'autoplay_stop', Date.now(), 1, 'Manual', false);
    dispatch(autoplayActions.stop());
  };

  const toggleOpen = (): void => {
    dispatch(autoplayActions.toggleOpen());
  };

  const launchAutoplay = (event: FormEvent<HTMLFormElement>): boolean => {
    event.preventDefault();

    // TODO: Refactor how autoplay limits are handled
    const validationPassed = limitsRef.current?.onSubmit(event);
    if (!validationPassed) {
      return false;
    }

    if (currentBet) {
      dispatch(autoplayActions.saveBet(currentBet));
    }

    const { amountValue } = event.target as HTMLFormElement;
    dispatch(autoplayActions.updateRoundCount(parseInt(amountValue.value, 10)));
    toggleOpen();

    if (!isInProgress) {
      dispatch(autoplayActions.start({ midRound: !isBetsOpen }));
      Logger.logEvent('click', 'autoplay_start', Date.now(), 1, amountValue.value, false);
    } else {
      dispatch(toastActions.createToast('AUTOPLAY_ROUNDS_MODIFIED'));
      Logger.logEvent('click', 'autoplay_modify', Date.now(), 1, amountValue.value, false);
    }

    return false;
  };

  return (
    <AutoPlayView
      totalBet={<CurrencyConnected value={totalBet} />}
      isInProgress={isInProgress}
      open={isOpen}
      currentAmount={roundsLeft}
      onSubmit={launchAutoplay}
      onStop={onStop}
      toggleOpen={toggleOpen}
      startEnabled={totalBet > 0}
      limitComponent={<AutoPlayLimitInputsConnected ref={limitsRef} />}
    />
  );
};
